import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMeetingService } from 'hooks/api/agents/meeting';
import GmModal from 'app/shared/modal/modal';
import styles from './calendar.module.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const STATUS_META = {
  scheduled: { label: 'Scheduled', cls: 'statusScheduled' },
  held: { label: 'Held', cls: 'statusHeld' },
  cancelled: { label: 'Cancelled', cls: 'statusCancelled' },
  no_show: { label: 'No show', cls: 'statusNoShow' }
};

const dayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const personName = (person = {}) =>
  [person.firstname, person.lastname].filter(Boolean).join(' ') || person.email || '';

const formatTime = (value) =>
  value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

const formatFullDate = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';

const buildWeeks = (year, month) => {
  const first_of_month = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first_of_month.getDay());
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
};

export const CalendarBoard = () => {
  const navigateTo = useNavigate();
  const { fetchMeetings } = useMeetingService();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view_date, setViewDate] = useState(() => new Date());
  const [selected_meeting, setSelectedMeeting] = useState(null);

  const year = view_date.getFullYear();
  const month = view_date.getMonth();

  const weeks = useMemo(() => buildWeeks(year, month), [year, month]);

  useEffect(() => {
    const range_start = new Date(weeks[0][0]);
    range_start.setHours(0, 0, 0, 0);
    const last_cell = weeks[weeks.length - 1][6];
    const range_end = new Date(last_cell);
    range_end.setHours(23, 59, 59, 999);

    const query_string = `time=${range_start.toISOString()}~${range_end.toISOString()}&sort_by=time&population=500`;

    setLoading(true);
    fetchMeetings({ query_string })
      .then(({ meetings: fetched }) => setMeetings(fetched || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const meetings_by_day = useMemo(() => {
    const map = {};
    meetings.forEach((meeting) => {
      if (!meeting?.time) return;
      const date = new Date(meeting.time);
      if (Number.isNaN(date.getTime())) return;
      const key = dayKey(date);
      (map[key] = map[key] || []).push(meeting);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
    );
    return map;
  }, [meetings]);

  const month_count = useMemo(
    () =>
      meetings.reduce((total, meeting) => {
        if (!meeting?.time) return total;
        const date = new Date(meeting.time);
        return date.getFullYear() === year && date.getMonth() === month ? total + 1 : total;
      }, 0),
    [meetings, year, month]
  );

  const today = new Date();

  const goToPreviousMonth = () => setViewDate(new Date(year, month - 1, 1));
  const goToNextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const goToToday = () => setViewDate(new Date());

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <div className={styles.headingBlock}>
          <h1 className={styles.monthTitle}>
            {MONTHS[month]} {year}
          </h1>
          <span className={styles.monthCount}>
            {month_count} {month_count === 1 ? 'meeting' : 'meetings'}
          </span>
        </div>
        <div className={styles.controls}>
          <button type="button" className={styles.todayBtn} onClick={goToToday}>
            Today
          </button>
          <div className={styles.navGroup}>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goToPreviousMonth}
              aria-label="Previous month"
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
        </div>
      </header>

      <div className={styles.weekdays}>
        {WEEKDAYS.map((day) => (
          <div key={day} className={styles.weekday}>
            {day}
          </div>
        ))}
      </div>

      <div className={styles.grid}>
        {weeks.map((week, week_index) => (
          <div key={week_index} className={styles.week}>
            {week.map((date) => {
              const in_month = date.getMonth() === month;
              const day_meetings = meetings_by_day[dayKey(date)] || [];
              const is_today = isSameDay(date, today);
              return (
                <div
                  key={dayKey(date)}
                  className={`${styles.day} ${in_month ? '' : styles.dayMuted} ${
                    is_today ? styles.dayToday : ''
                  }`}
                >
                  <span className={`${styles.dayNumber} ${is_today ? styles.dayNumberToday : ''}`}>
                    {date.getDate()}
                  </span>
                  <div className={styles.dayMeetings}>
                    {day_meetings.map((meeting) => {
                      const status = meeting.status || 'scheduled';
                      const meta = STATUS_META[status] || STATUS_META.scheduled;
                      return (
                        <button
                          key={meeting.id || meeting._id}
                          type="button"
                          className={`${styles.meetingChip} ${styles[meta.cls]}`}
                          onClick={() => setSelectedMeeting(meeting)}
                          title={meeting.title || 'Meeting'}
                        >
                          <span className={styles.chipTime}>{formatTime(meeting.time)}</span>
                          <span className={styles.chipTitle}>{meeting.title || 'Meeting'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {loading && <p className={styles.loading}>Loading meetings…</p>}

      <GmModal
        title="Meeting"
        show_title
        show_modal={!!selected_meeting}
        onClose={() => setSelectedMeeting(null)}
      >
        {selected_meeting && <MeetingDetails meeting={selected_meeting} onNavigate={navigateTo} />}
      </GmModal>
    </div>
  );
};

const MeetingDetails = ({ meeting, onNavigate }) => {
  const status = meeting.status || 'scheduled';
  const meta = STATUS_META[status] || STATUS_META.scheduled;
  const subject = meeting.subject || {};
  const invitee_name = personName(meeting.invitee);
  const host_name = personName(meeting.host);

  const openSubject = () => {
    if (subject.kind === 'tenant') onNavigate(`/tenants/${subject.id}/dashboard`);
    else if (subject.kind === 'lead') onNavigate('/customers/leads');
  };

  return (
    <div className={styles.details}>
      <div className={styles.detailsHeader}>
        <h3 className={styles.detailsTitle}>{meeting.title || 'Meeting'}</h3>
        <span className={`${styles.statusBadge} ${styles[meta.cls]}`}>{meta.label}</span>
      </div>
      <p className={styles.detailsWhen}>{formatFullDate(meeting.time)}</p>

      <dl className={styles.detailsList}>
        {invitee_name && (
          <div className={styles.detailsRow}>
            <dt>Invitee</dt>
            <dd>
              {invitee_name}
              {meeting.invitee?.email ? ` · ${meeting.invitee.email}` : ''}
            </dd>
          </div>
        )}
        {host_name && (
          <div className={styles.detailsRow}>
            <dt>Host</dt>
            <dd>{host_name}</dd>
          </div>
        )}
        {subject.name && (
          <div className={styles.detailsRow}>
            <dt>{subject.kind === 'tenant' ? 'Tenant' : 'Lead'}</dt>
            <dd>
              <button type="button" className={styles.linkBtn} onClick={openSubject}>
                {subject.name}
              </button>
            </dd>
          </div>
        )}
      </dl>

      {meeting.link && (
        <a
          className={styles.joinLink}
          href={meeting.link}
          target="_blank"
          rel="noreferrer noopener"
        >
          Join Meeting
        </a>
      )}
    </div>
  );
};

export default CalendarBoard;
